import { ReactElement, useMemo, useState } from 'react';
import { Modal, PrimaryButton, SecondaryButton, Typography } from '@weareopenr/component-library';
import ModalImg from 'src/assets/images/section-modal.png';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import { MenuSetMenusStep } from 'src/components/CreateMenuSetModal/MenuSetMenusStep';
import { IMenuSetDetail } from 'src/api/getMenuSet';
import { EntitySource } from 'src/constants';
import { useMenus } from 'src/hooks/useMenus';
import { getCompatibleMenus } from 'src/utils/menuSets/getCompatibleMenus';
import { getMenuSetSource } from 'src/utils/menuSets/getMenuSetSource';
import styles from './AddMenusToMenuSetModal.module.scss';

interface IAddMenusToMenuSetModalProps {
  open: boolean;
  menuSet: IMenuSetDetail;
  closeModal: () => void;
  onAddMenus: (menuIds: string[]) => void;
  isSubmitting?: boolean;
}

const AddMenusToMenuSetContent = ({
  menuSet,
  closeModal,
  onAddMenus,
  isSubmitting,
}: Omit<IAddMenusToMenuSetModalProps, 'open'>): ReactElement => {
  const { data: menus, isLoading } = useMenus();
  const [selectedMenuIds, setSelectedMenuIds] = useState<string[]>([]);

  const [source, sourceIds] = useMemo(
    () =>
      getMenuSetSource(
        menuSet.ranges.map(({ range_id }) => range_id),
        (menuSet.source_menu_labels ?? []).map(({ menu_label_id }) => menu_label_id),
      ),
    [menuSet.ranges, menuSet.source_menu_labels],
  );
  const availableMenus = useMemo(() => {
    const menuIdsInSet = new Set(menuSet.menus.map(({ menu_id }) => menu_id));
    return getCompatibleMenus(menus ?? [], source, sourceIds).filter(({ menuId }) => !menuIdsInSet.has(menuId));
  }, [menus, menuSet.menus, source, sourceIds]);

  return (
    <div className={styles['content']}>
      <Typography size="xxlarge" className={styles['title']}>
        Add menu(s) to <strong>{menuSet.name}</strong>
      </Typography>

      <div className={styles['list']}>
        <MenuSetMenusStep
          menus={availableMenus}
          selectedMenuIds={selectedMenuIds}
          onMenusChange={setSelectedMenuIds}
          isLoading={isLoading}
          sourceMenuLabelIds={source === EntitySource.MENU_LABELS ? sourceIds : undefined}
        />
      </div>

      <div className={styles['footer']}>
        <ButtonContainer
          margin="none"
          left={
            <SecondaryButton buttonStyle="outline" type="button" size="full-width" onClick={closeModal}>
              Back
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              type="button"
              size="full-width"
              loading={isSubmitting}
              disabled={selectedMenuIds.length === 0 || isSubmitting}
              onClick={() => onAddMenus(selectedMenuIds)}
            >
              Add Menu(s)
            </PrimaryButton>
          }
        />
      </div>
    </div>
  );
};

export const AddMenusToMenuSetModal = ({ open, closeModal, ...props }: IAddMenusToMenuSetModalProps): ReactElement => (
  <Modal
    open={open}
    closeModal={closeModal}
    image={ModalImg}
    imageAlt="Picture of four beautifully displayed breakfast plates"
  >
    <AddMenusToMenuSetContent closeModal={closeModal} {...props} />
  </Modal>
);

export default AddMenusToMenuSetModal;
